<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Health Indicator -->
    <div
      :class="[
        'fixed top-5 right-5 z-50 px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-2 shadow-md border transition-all duration-200',
        healthStatus === 'healthy'
          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-500'
          : healthStatus === 'unhealthy'
            ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-500'
            : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border-yellow-500'
      ]"
    >
      <span
        :class="[
          'w-2 h-2 rounded-full',
          healthStatus === 'healthy'
            ? 'bg-green-500'
            : healthStatus === 'unhealthy'
              ? 'bg-red-500'
              : 'bg-yellow-500 animate-pulse'
        ]"
      ></span>
      <span>{{
        healthStatus === 'healthy'
          ? 'Healthy'
          : healthStatus === 'unhealthy'
            ? 'Unhealthy'
            : 'Checking...'
      }}</span>
    </div>

    <!-- Header -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-4'
      ]"
    >
      <div class="flex items-center justify-between">
        <div>
          <h1
            :class="[
              theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
              'text-2xl font-bold flex items-center gap-3'
            ]"
          >
            <span class="text-3xl">👥</span>
            Task Sentinel
          </h1>
          <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'mt-1 text-sm']">
            Manage and visualize ClickUp teams configuration
          </p>
        </div>
        <div class="flex items-center space-x-3">
          <!-- Refresh Button -->
          <button
            :disabled="loading"
            :class="[
              theme.isDark.value
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                : 'bg-white hover:bg-gray-50 text-gray-700',
              'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors flex items-center',
              loading ? 'opacity-50 cursor-not-allowed' : ''
            ]"
            @click="loadTeams"
          >
            <svg
              :class="[loading ? 'animate-spin' : '', 'w-4 h-4 mr-2']"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {{ loading ? 'Loading...' : 'Refresh' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="p-6 space-y-6">
      <!-- Filters -->
      <TaskSentinelFilters
        :filters="filters"
        :loading="loading"
        :result-count="filteredTeams.length"
        @update-search="(v) => (filters.search = v)"
        @update-offset="(v) => (filters.offset = v)"
        @update-limit="(v) => (filters.limit = v)"
        @load-data="loadTeams"
        @reset="resetFilters"
      />

      <!-- Stats Summary -->
      <div
        v-if="teams.length > 0"
        class="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg p-4'
          ]"
        >
          <div class="text-2xl font-bold text-gray-900 dark:text-white">{{ stats.total }}</div>
          <div class="text-sm text-gray-500 dark:text-gray-400">Total Teams</div>
        </div>
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg p-4'
          ]"
        >
          <div class="text-2xl font-bold text-green-600 dark:text-green-400">
            {{ stats.active }}
          </div>
          <div class="text-sm text-gray-500 dark:text-gray-400">Active</div>
        </div>
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg p-4'
          ]"
        >
          <div class="text-2xl font-bold text-red-600 dark:text-red-400">{{ stats.deleted }}</div>
          <div class="text-sm text-gray-500 dark:text-gray-400">Deleted</div>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="error"
        :class="[
          theme.isDark.value ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200',
          'border rounded-md p-4'
        ]"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800 dark:text-red-200">Error loading teams</h3>
            <div class="mt-2 text-sm text-red-700 dark:text-red-300">{{ error }}</div>
            <button
              class="mt-2 px-3 py-1 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-100 rounded text-sm"
              @click="loadTeams"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading && teams.length === 0"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8'
        ]"
      >
        <div class="flex items-center justify-center">
          <svg
            class="animate-spin h-8 w-8 text-blue-500"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span class="ml-3 text-lg text-gray-600 dark:text-gray-300">Loading teams...</span>
        </div>
      </div>

      <!-- Teams Table -->
      <TeamsTable
        v-else-if="filteredTeams.length > 0"
        :teams="filteredTeams"
        :sort-config="sortConfig"
        @sort="handleSort"
        @view="navigateToTeam"
        @duplicate="duplicateTeam"
      />

      <!-- Empty State -->
      <div
        v-else-if="!loading && !error"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8 text-center'
        ]"
      >
        <div class="text-6xl mb-4 opacity-40">📋</div>
        <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No teams found</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {{
            filters.search
              ? 'No teams match your search criteria.'
              : 'Click "Load Data" to fetch teams.'
          }}
        </p>
      </div>

      <!-- Pagination -->
      <div
        v-if="teams.length > 0"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-4 flex flex-wrap items-center justify-between gap-4'
        ]"
      >
        <div class="flex items-center gap-3">
          <button
            :disabled="filters.offset === 0"
            :class="[
              'px-4 py-2 border rounded-md text-sm font-medium transition-colors',
              filters.offset === 0
                ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700'
                : theme.isDark.value
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-200 border-gray-600'
                  : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
            ]"
            @click="previousPage"
          >
            ← Previous
          </button>
          <span class="text-sm text-gray-600 dark:text-gray-400">
            Page {{ currentPage }} • Showing {{ filters.offset + 1 }}-{{
              filters.offset + teams.length
            }}
            teams
          </span>
          <button
            :disabled="teams.length < filters.limit"
            :class="[
              'px-4 py-2 border rounded-md text-sm font-medium transition-colors',
              teams.length < filters.limit
                ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700'
                : theme.isDark.value
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-200 border-gray-600'
                  : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
            ]"
            @click="nextPage"
          >
            Next →
          </button>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-sm text-gray-600 dark:text-gray-400">Jump to:</span>
          <input
            v-model.number="jumpToPageValue"
            type="number"
            min="1"
            placeholder="Page"
            class="w-20 px-2 py-1 border rounded-md text-sm text-center bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
            @keypress.enter="jumpToPage"
          />
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div
      v-if="toast.show"
      :class="[
        'fixed top-20 right-5 z-50 px-5 py-3 rounded-lg shadow-lg border font-medium max-w-md flex items-center gap-3 animate-slide-in',
        toast.type === 'success'
          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-500'
          : toast.type === 'error'
            ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-500'
            : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-500'
      ]"
    >
      {{ toast.message }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, inject, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTheme } from '../../utils/useTheme'
import TaskSentinelFilters from './components/TaskSentinelFilters.vue'
import TeamsTable from './components/TeamsTable.vue'
import { taskSentinelService } from '../../services/task-sentinel'
import type {
  Team,
  TaskSentinelFilters as FiltersType,
  SortConfig,
  ToastType
} from '../../types/task-sentinel'
import { logger } from '../../utils/logger'

const router = useRouter()
const theme = inject('theme', useTheme())

// State
const teams = ref<Team[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const healthStatus = ref<'healthy' | 'unhealthy' | 'checking'>('checking')
const jumpToPageValue = ref<number | null>(null)

// Filters
const filters = reactive<FiltersType>({
  search: '',
  apiBaseUrl: localStorage.getItem('taskSentinel_apiUrl') || '',
  offset: 0,
  limit: parseInt(localStorage.getItem('taskSentinel_limit') || '50')
})

// Sort configuration
const sortConfig = reactive<SortConfig>({
  column: -1,
  direction: 'asc'
})

// Toast notification
const toast = reactive({
  show: false,
  message: '',
  type: 'info' as ToastType
})

// Health check interval
let healthCheckInterval: ReturnType<typeof setInterval> | null = null

// Computed
const filteredTeams = computed(() => {
  if (!filters.search) return teams.value

  const searchLower = filters.search.toLowerCase()
  return teams.value.filter((team) => {
    const searchableText = [
      team.name,
      team.id,
      team.standupId,
      team.roadmapBoardId,
      team.parentGroup,
      ...(team.managers?.map((m) => m.emailPrefix) || []),
      ...(team.devs?.map((d) => d.emailPrefix) || []),
      ...(team.devs?.map((d) => d.name) || [])
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return searchableText.includes(searchLower)
  })
})

const stats = computed(() => ({
  total: filteredTeams.value.length,
  active: filteredTeams.value.filter((t) => !t.deleted).length,
  deleted: filteredTeams.value.filter((t) => t.deleted).length
}))

const currentPage = computed(() => Math.floor(filters.offset / filters.limit) + 1)

// Methods
const showToast = (message: string, type: ToastType = 'info') => {
  toast.message = message
  toast.type = type
  toast.show = true
  setTimeout(() => {
    toast.show = false
  }, 3000)
}

const loadTeams = async () => {
  try {
    loading.value = true
    error.value = null

    logger.log('Loading teams with params:', { offset: filters.offset, limit: filters.limit })

    const data = await taskSentinelService.fetchTeams({
      offset: filters.offset,
      limit: filters.limit
    })

    teams.value = data
    savePreferences()
    showToast(`Loaded ${data.length} team${data.length === 1 ? '' : 's'}`, 'success')

    logger.log('Teams loaded:', data.length)
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load teams'
    showToast(error.value, 'error')
    logger.error('Error loading teams:', err)
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  filters.search = ''
  filters.offset = 0
  filters.limit = 50
  sortConfig.column = -1
  sortConfig.direction = 'asc'
  savePreferences()
  loadTeams()
}

const handleSort = (column: number) => {
  if (sortConfig.column === column) {
    sortConfig.direction = sortConfig.direction === 'asc' ? 'desc' : 'asc'
  } else {
    sortConfig.column = column
    sortConfig.direction = 'asc'
  }
}

const navigateToTeam = (team: Team) => {
  router.push(`/task-sentinel/${team.id}`)
}

const duplicateTeam = async (team: Team) => {
  try {
    const newId = taskSentinelService.generateTeamId()
    const duplicateData = {
      ...team,
      id: undefined,
      name: `Copy of ${team.name}`,
      dateAdded: undefined,
      dateUpdated: undefined
    }

    // Remove undefined fields
    Object.keys(duplicateData).forEach((key) => {
      if (duplicateData[key as keyof typeof duplicateData] === undefined) {
        delete duplicateData[key as keyof typeof duplicateData]
      }
    })

    await taskSentinelService.createTeam(newId, duplicateData)
    showToast(`Team duplicated successfully! New name: Copy of ${team.name}`, 'success')

    // Reload teams
    setTimeout(() => loadTeams(), 500)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to duplicate team'
    showToast(message, 'error')
    logger.error('Error duplicating team:', err)
  }
}

const previousPage = () => {
  filters.offset = Math.max(0, filters.offset - filters.limit)
  loadTeams()
}

const nextPage = () => {
  filters.offset += filters.limit
  loadTeams()
}

const jumpToPage = () => {
  if (jumpToPageValue.value && jumpToPageValue.value >= 1) {
    filters.offset = (jumpToPageValue.value - 1) * filters.limit
    jumpToPageValue.value = null
    loadTeams()
  }
}

const checkHealth = async () => {
  healthStatus.value = 'checking'
  const isHealthy = await taskSentinelService.checkHealth()
  healthStatus.value = isHealthy ? 'healthy' : 'unhealthy'
}

const savePreferences = () => {
  localStorage.setItem('taskSentinel_limit', filters.limit.toString())
  localStorage.setItem('taskSentinel_search', filters.search)
}

const loadPreferences = () => {
  const savedSearch = localStorage.getItem('taskSentinel_search')
  if (savedSearch) {
    filters.search = savedSearch
  }
}

// Watch for search changes to save
watch(
  () => filters.search,
  () => {
    savePreferences()
  }
)

// Keyboard shortcuts
const handleKeydown = (e: KeyboardEvent) => {
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault()
    const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement
    if (searchInput) searchInput.focus()
  }
  if (e.key === 'Escape') {
    filters.search = ''
  }
}

// Lifecycle
onMounted(() => {
  loadPreferences()
  loadTeams()
  checkHealth()
  healthCheckInterval = setInterval(checkHealth, 15000)
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval)
  }
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
@keyframes slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in {
  animation: slide-in 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}
</style>
